import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { DataItem, navigateTo } from 'scrivito'

import { DataItemForm } from '../DataItemForm'
import { RelationalDataItemSublist } from '../RelationalDataItemSublist'
import { RELATION_CONFIGS } from './configs'
import { StringText } from '@/Atomic/Atoms/StringText'
import { Button } from '@/Atomic/Molecules/Button'
import { Tooltip } from '@/Atomic/Molecules/Tooltip'
import DropdownMenu from '@/Atomic/Organisms/DropdownMenu'
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
} from '@/Atomic/Templates/Drawer'
import { FollowUpsSublist } from '@/Components/Activity/FollowUpsSublist'
import { LogPhoneCallButton } from '@/Components/Activity/LogPhoneCall'
import { NewFollowUpActivityDrawer } from '@/Components/Activity/NewFollowUpActivityDrawer/NewFollowUpActivityDrawer'
import { ConfirmationModal } from '@/Components/shared/modals/ConfirmationModal'
import { DataClassName } from '@/Data/const'
import { useFlashSublistItem } from '@/hooks/useFlashSublistItem'
import { useModalHandler } from '@/hooks/useModalHandler'
import { copyUrl } from '@/utils/copyUrl'
import { useDataItemFormHandler } from '@/utils/CRUD/hooks/useDataItemFormHandler'
import { getDeleteModalSubtitle } from '@/Widgets/DXPDetails/utils/deleteModalSubtitle'
import { getDeleteModalTitle } from '@/Widgets/DXPDetails/utils/deleteModalTitle'

import './DataItemDrawer.scss'

interface DataItemDrawerProps {
  dataItem: DataItem
  isOpen: boolean
  close: () => void
  startInEditMode?: boolean
  openDrawerId?: (id?: string) => void
  nextId?: string
  previousId?: string
}

export const DataItemDrawer: React.FC<DataItemDrawerProps> = ({
  dataItem,
  isOpen,
  close,
  startInEditMode,
  openDrawerId,
  nextId,
  previousId,
}) => {
  const { t } = useTranslation()
  const [showTip, setShowTip] = useState(false)
  const [copyUrlTooltip, setCopyUrlTooltip] = useState('')
  const { flashId: flashFollowUpId, flash: flashFollowUp } =
    useFlashSublistItem()

  const {
    methods,
    onFormSubmit,
    handleSave,
    isEditing,
    abortEditing,
    setIsEditing,
    toggleEdit,
  } = useDataItemFormHandler(dataItem)

  useEffect(() => {
    if (isOpen && startInEditMode) {
      setIsEditing(true)
    }
  }, [isOpen, startInEditMode, setIsEditing])

  const { isOpen: modalIsOpen, closeModal, openModal } = useModalHandler()
  const dataClassName = dataItem.dataClassName()!
  const hasFollowUpActivity = dataClassName === DataClassName.ACTIVITY
  const supportsPhoneCall =
    dataClassName === DataClassName.PERSON ||
    dataClassName === DataClassName.ACTIVITY

  const onCopyURLClick = () => {
    copyUrl()
      .then(() => {
        setCopyUrlTooltip(t('tooltip.urlCopy.success'))
        setShowTip(true)
      })
      .catch(() => {
        setCopyUrlTooltip(t('tooltip.urlCopy.error'))
        setShowTip(true)
      })
      .finally(() =>
        setTimeout(() => {
          setShowTip(false)
        }, 1500),
      )
  }

  return (
    <Drawer isOpen={isOpen} close={close} direction="right">
      <DrawerHeader>
        <div className="header item">
          <Button
            variant="icon"
            icon="cross"
            tooltip={t('tooltip.close')}
            onClick={close}
          />
          <Button
            variant="icon"
            icon="chevron-up"
            tooltip={t('tooltip.previous')}
            onClick={openDrawerId ? () => openDrawerId(previousId) : undefined}
            disabled={!previousId}
            hidden={!openDrawerId}
          />
          <Button
            variant="icon"
            icon="chevron-down"
            tooltip={t('tooltip.next')}
            onClick={openDrawerId ? () => openDrawerId(nextId) : undefined}
            disabled={!nextId}
            hidden={!openDrawerId}
          />
        </div>
        <div className="header item">
          {supportsPhoneCall && (
            <LogPhoneCallButton
              dataItem={dataItem}
              buttonClassName="phone-call-drawer-button"
            />
          )}
          {hasFollowUpActivity && (
            <NewFollowUpActivityDrawer
              originActivity={dataItem}
              isDrawerButton
              navigateOnCreated={false}
              onCreated={(followUpActivity) =>
                flashFollowUp(followUpActivity.id())
              }
            />
          )}
          <Button
            variant="icon"
            icon="edit"
            tooltip={t('tooltip.edit')}
            onClick={toggleEdit}
          />
          <DropdownMenu
            data-tooltip-id="copied-tooltip"
            buttonClassName="borderless button"
            menuItems={[
              {
                icon: 'info-circle',
                label: t('common.seeDetails'),
                onClick: () => navigateTo(dataItem),
              },
              {
                icon: 'copy',
                label: t('tooltip.copyUrl'),
                onClick: onCopyURLClick,
              },
              {
                icon: 'bin',
                label: t('common.delete'),
                onClick: openModal,
                type: 'danger',
              },
            ]}
          />
          <ConfirmationModal
            variant="danger"
            isOpen={modalIsOpen}
            close={closeModal}
            confirm={() => dataItem.delete().then(() => close())}
            title={t(getDeleteModalTitle(dataClassName))}
            subtitle={t(getDeleteModalSubtitle(dataClassName))}
          />
        </div>
      </DrawerHeader>
      <DrawerBody>
        <DataItemForm
          dataItem={dataItem}
          methods={methods}
          onFormSubmit={onFormSubmit}
          isEditing={isEditing}
        />
        {hasFollowUpActivity && (
          <div className="follow-ups-drawer-container">
            <StringText
              value={t('pages.activity.followUpsTitle')}
              variant="title"
            />
            <FollowUpsSublist
              originActivity={dataItem}
              variant="drawer"
              flashFollowUpId={flashFollowUpId || undefined}
              onOpenFollowUp={(followUp) => navigateTo(followUp)}
            />
          </div>
        )}
        {RELATION_CONFIGS[dataClassName]?.map((config) => (
          <RelationalDataItemSublist
            key={`${dataItem.id()} - ${config}`}
            id={dataItem.id()}
            configKey={config}
          />
        ))}
      </DrawerBody>
      {isEditing && (
        <DrawerFooter align="right">
          <Button variant="outlined" onClick={abortEditing} size="lg">
            {t('common.cancel')}
          </Button>
          <Button icon="disk" onClick={handleSave} size="lg">
            {t('common.saveChanges')}
          </Button>
        </DrawerFooter>
      )}
      <Tooltip
        anchorSelect=".o-menu-button"
        content={copyUrlTooltip}
        isOpen={showTip}
      />
    </Drawer>
  )
}
